import type { Theme } from '@mui/material/styles';
import type { ChipColor } from '@/types/chip';
import { useGetList } from 'react-admin';
import { useState } from 'react';
import { useMediaQuery, Box, Typography, Paper, Chip, Table, TableBody, TableCell, TableHead, TableRow, Avatar, TablePagination } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Security } from '@mui/icons-material';
import { useEmployeeMap, getEmployeeName } from '@/hooks/useEmployeeMap';
import { ListPageHeader } from '@/components/ListPageHeader';

interface RepItem {
  id: string;
  status: string;
  employee?: { id: string };
  areaRepresented?: string;
  expiryDate?: string;
}

export const RepresentativeList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const { data: representatives, isLoading } = useGetList('ohs-representatives', { pagination: { page: page + 1, perPage: rowsPerPage } });
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const employeeMap = useEmployeeMap();
  const [now] = useState(() => Date.now());

  const stats = {
    total: representatives?.length ?? 0,
    active: representatives?.filter((r: RepItem) => r.status === 'Active').length ?? 0,
    expired: representatives?.filter((r: RepItem) => {
      const days = Math.ceil((new Date(r.expiryDate ?? '').getTime() - now) / (1000 * 60 * 60 * 24));
      return days <= 0;
    }).length ?? 0,
    pending: representatives?.filter((r: RepItem) => r.status === 'Pending' || r.status === 'PendingTraining').length ?? 0,
  };

  const pieData = [
    { label: 'Active', value: stats.active },
    { label: 'Expired', value: stats.expired },
    { label: 'Pending', value: stats.pending },
  ].filter(d => d.value > 0);

  if (isMobile) {
    return (
      <Box sx={{ p: 2 }}>
        <ListPageHeader
          title="OHS Representatives"
          metrics={[
            { label: 'Active', value: String(stats.active), color: 'success.main' },
            { label: 'Expired', value: String(stats.expired), color: 'error.main' },
            { label: 'Pending', value: String(stats.pending), color: 'warning.main' },
          ]}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
          {representatives?.map((rep: RepItem) => {
            const days = Math.ceil((new Date(rep.expiryDate ?? '').getTime() - now) / (1000 * 60 * 60 * 24));
            const statusColor = days <= 0 ? 'error' : days <= 60 ? 'warning' : 'success';
            return (
              <Paper key={rep.id} sx={{ p: 2 }} onClick={() => navigate(`/ohs-representatives/${rep.id}/show`)}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ width: 40, height: 40, bgcolor: statusColor === 'success' ? 'success.main' : statusColor === 'warning' ? 'warning.main' : 'error.main', fontSize: 14 }}>
                      <Security sx={{ fontSize: 20 }} />
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>{getEmployeeName(employeeMap, rep.employee?.id)}</Typography>
                    </Box>
                  </Box>
                  <Chip label={rep.status} size="small" color={statusColor as ChipColor} />
                </Box>
                <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">{rep.areaRepresented}</Typography>
                  <Typography variant="caption" color="text.secondary">·</Typography>
                  <Typography variant="caption" color="text.secondary">Expires: {rep.expiryDate}</Typography>
                </Box>
              </Paper>
            );
          })}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <ListPageHeader
        title="OHS Representatives"
        metrics={[
          { label: 'Active', value: String(stats.active), color: 'success.main' },
          { label: 'Expired', value: String(stats.expired), color: 'error.main' },
          { label: 'Pending', value: String(stats.pending), color: 'warning.main' },
          { label: 'Total', value: String(stats.total), color: 'primary.main' },
        ]}
        pieChartData={pieData}
        pieChartTitle="Certification Status"
      />
      <Paper sx={{ mt: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 600 }}>Representative</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Area</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Expires</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {representatives?.map((rep: RepItem) => {
              const days = Math.ceil((new Date(rep.expiryDate ?? '').getTime() - now) / (1000 * 60 * 60 * 24));
              const statusColor = days <= 0 ? 'error' : days <= 60 ? 'warning' : 'success';
              return (
                <TableRow
                  key={rep.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/ohs-representatives/${rep.id}/show`)}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 28, height: 28, bgcolor: statusColor === 'success' ? 'success.main' : statusColor === 'warning' ? 'warning.main' : 'error.main', fontSize: 12 }}>
                        <Security sx={{ fontSize: 14 }} />
                      </Avatar>
                      <Typography variant="body2" fontWeight={500}>{getEmployeeName(employeeMap, rep.employee?.id)}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{rep.areaRepresented}</Typography>
                  </TableCell>
                  <TableCell>
                  <Chip label={rep.status} size="small" color={statusColor as ChipColor} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color={days <= 0 ? 'error.main' : days <= 60 ? 'warning.main' : 'text.primary'}>
                      {rep.expiryDate}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={representatives?.length ?? 0}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
        {(!representatives || representatives.length === 0) && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Security sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
            <Typography color="text.secondary">No representatives found</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};
